import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useModuleVisibility } from '../../hooks/useModuleVisibility';
import { SlidersHorizontalIcon, StarIcon, PlusIcon } from '@phosphor-icons/react';
import { InjuryRegistrationModal } from '../components/InjuryRegistrationForm/InjuryRegistrationModal';
import { Spinner, NativeSelect } from '@chakra-ui/react';
import { Table as TableIcon, BarChart3, LayoutGrid, Settings2 } from 'lucide-react';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { ITeam } from '../../models/Team';
import { useEndpoints } from '../../hooks/useEndpoints';
import { useInjuryEndpoints } from '../../hooks/useInjuryEndpoints';
import { ShowByRole } from '../../shared/ShowByRole';
import { IInsightsMetric } from '../../models/injury';
import { TeamInjuryCharts } from '../components/TeamInjuryCharts/TeamInjuryCharts';
import { TeamInjuryTimeline } from '../components/TeamInjuryTimeline/TeamInjuryTimeline';
import { TeamInsightsCard } from '../components/TeamInjuryCharts/TeamInsightsCard';
import { ChartErrorState } from '../components/AthleteInjuryCharts/charts/_shared/ChartErrorState';
import { ChartEmptyState } from '../components/AthleteInjuryCharts/charts/_shared/ChartEmptyState';
import { InjuryTimeline } from '../components/InjuryTimeline/InjuryTimeline';
import { AthleteInjuryCharts } from '../components/AthleteInjuryCharts/AthleteInjuryCharts';
import { AnamnesisCard } from '../components/AnamnesisCard';
import { getLabelFromOptions } from '../../lib/DurationPicker/utils';
import { LEVELS_SPORT_OPTIONS } from '../../constants/config';
import InformationsTab from '../../components/DetailTeamPage/InformationsTab/InformationsTab';
import AthletesTab from '../../components/DetailTeamPage/AthletesTab/AthletesTab';
import { DataProvider } from '../context/DataContext';
import TeamReadinessTab from '../components/TeamReadinessTab';
import TeamActivitiesTab from '../components/TeamActivitiesTab';
import TestingView from '../components/TestingView';
import { TargetIcon } from '@phosphor-icons/react';
import TeamProfilingTab from '../../components/DetailTeamPage/TeamProfilingTab/TeamProfilingTab';
import TeamSettingsModal from '../components/TeamSettingsModal';
import TestConfigModal from '../components/TestConfigModal';
import TeamProgrammingTab from '../../components/ProgrammingPage/TeamProgrammingTab/TeamProgrammingTab';
import { ProgrammingTabs } from '../components/ProgrammingTabs/ProgrammingTabs';
import TeamCalendarTab from '../components/TeamCalendarTab';
import TeamBodyMapHeatmap from '../components/TeamBodyMapHeatmap/TeamBodyMapHeatmap';
import TeamBodyMapAnalytics from '../components/TeamBodyMapAnalytics/TeamBodyMapAnalytics';
import AFITab from '../components/AFI/AFITab';
import { CarichiTeamTab } from '../components/CarichiTeamTab';
import { TeamEffortTab } from '../components/effort/TeamEffortTab';
import { TeamCustomMetricsSection } from '../../pages/DetailTeamPage/components/TeamCustomMetricsSection';
import AllenamentiPage from '../../pages/AllenamentiPage/AllenamentiPage';
import toast from 'react-hot-toast';
import CustomButton from '../../shared/CustomButton/CustomButton';
import InjuryPeriodFilter, { InjuryPeriod } from '../../shared/InjuryPeriodFilter/InjuryPeriodFilter';
import LeopartLoader from '../../shared/LeopartLoader/LeopartLoader';
import styles from './TeamDetail.module.scss';

interface TeamDetailProps {
  team: ITeam<any>;
  teamQuery: UseQueryResult<ITeam, Error>;
  onClickStar: () => void;
  isStarLoading: boolean;
  goBack: () => void;
}

/** Tutti i tab disponibili (senza filtri di visibilità) */
const ALL_TABS = ['panoramica', 'atleti', 'attivita', 'readiness', 'testing', 'programmazione', 'allenamenti', 'calendario', 'carichi', 'sforzo', 'storico-clinico', 'fastidi', 'afi', 'metriche-custom'] as const;
type ActiveTab = typeof ALL_TABS[number];

/** Mappa tab-id → chiave feature-flag */
const TEAM_TAB_VISIBILITY_KEYS: Record<ActiveTab, string> = {
  'panoramica': 'team-detail-panoramica',
  'atleti': 'team-detail-atleti',
  'attivita': 'team-detail-attivita',
  'readiness': 'team-detail-readiness',
  'testing': 'team-detail-testing',
  'programmazione': 'team-detail-programmazione',
  'allenamenti': 'team-detail-allenamenti',
  'calendario': 'team-detail-calendario',
  'carichi': 'team-detail-carichi',
  'sforzo': 'team-detail-sforzo',
  'storico-clinico': 'team-detail-storico-clinico',
  'fastidi': 'team-detail-fastidi',
  'afi': 'team-detail-afi',
  'metriche-custom': 'team-detail-metriche-custom',
};

export default function TeamDetail({ team, teamQuery, onClickStar, isStarLoading, goBack }: TeamDetailProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isTabVisible } = useModuleVisibility();

  // Tab visibili secondo i feature-flag runtime
  const visibleTabs = ALL_TABS.filter((t) => isTabVisible(TEAM_TAB_VISIBILITY_KEYS[t]));

  // Legge la tab iniziale dal query param ?tab= (es. al ritorno dalla sessione edit)
  const initialTab: ActiveTab = (() => {
    const tabParam = searchParams.get('tab');
    if ((ALL_TABS as readonly string[]).includes(tabParam ?? '')) return tabParam as ActiveTab;
    // Default 'panoramica' (#18, hub) SE visibile per il tenant; altrimenti il
    // primo tab attivo (evita di montare un tab nascosto dai feature-flag).
    if (visibleTabs.includes('panoramica')) return 'panoramica';
    return visibleTabs[0] ?? 'readiness';
  })();

  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);

  /** Cambia tab e persiste nel query param (replace evita di inquinare la history) */
  const handleSetActiveTab = (tab: ActiveTab) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  };

  // Se il tab attivo viene nascosto da un flag, spostati al primo visibile
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      handleSetActiveTab(visibleTabs[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs.join(','), activeTab]);

  const [injuryPeriod, setInjuryPeriod] = useState<InjuryPeriod>('all');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [testingViewMode, setTestingViewMode] = useState<'entry' | 'analytics' | 'profiling'>('entry');
  const [readinessViewMode, setReadinessViewMode] = useState<'matrix' | 'card'>('matrix');
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isTestConfigOpen, setIsTestConfigOpen] = useState(false);
  const [fastidiSubTab, setFastidiSubTab] = useState<'heatmap' | 'analytics'>('heatmap');
  const [addInjuryAthleteId, setAddInjuryAthleteId] = useState<string>('');
  const [addInjuryOpen, setAddInjuryOpen] = useState(false);

  const teamAthletes = useMemo(() => {
    return (team.users ?? []) as Array<{ _id: string; firstName: string; lastName: string }>;
  }, [team.users]);

  const injuryEndpoints = useInjuryEndpoints();
  const { data: insightsData, isLoading: insightsLoading, isError: insightsError, refetch: insightsRefetch } = useQuery<IInsightsMetric>({
    queryKey: ['injury', 'team', team._id, 'insights', injuryPeriod],
    queryFn: async () => {
      const { data } = await injuryEndpoints.getTeamInsights(team._id, injuryPeriod !== 'all' ? { period: injuryPeriod } : undefined);
      return data;
    },
    enabled: activeTab === 'storico-clinico',
    staleTime: 1000 * 60 * 5,
  });

  const { deleteTeam } = useEndpoints();

  const handleDeleteTeam = async () => {
    const id = toast.loading(`Rimozione team ${team.name} in corso...`, { duration: 20000 });
    try {
      await deleteTeam(team._id);
      toast.success(`${team.name} eliminato`, { id });
      goBack();
    } catch {
      toast.error('Errore durante l\'eliminazione del team', { id });
    }
  };

  const spaceType: string = (team.spaceId as any)?.type ?? '';
  const levelOptions = (LEVELS_SPORT_OPTIONS as Record<string, { value: string; label: string }[]>)[spaceType] ?? [];
  const levelLabel = getLabelFromOptions(levelOptions, team.level) || team.level;
  const initials = team.name?.slice(0, 2).toUpperCase() ?? '??';

  return (
    <DataProvider>
    <div className={styles.pageWrapper}>
      <div className={styles.scrollArea}>
        <div className={styles.inner}>
          {/* Breadcrumb */}
          <div className={styles.breadcrumb}>
            <p className={styles.breadcrumbMuted} style={{ cursor: 'pointer' }} onClick={goBack}>
              Dettaglio team
            </p>
          </div>

          {/* Title */}
          <div className={styles.titleRow}>
            <div className={styles.titleLeft}>
              {/* Avatar */}
              <div className={styles.avatarWrapper}>
                <div className={styles.avatarInner}>
                  {team.image ? (
                    <img
                      alt={team.name}
                      className={styles.avatarImg}
                      src={`${process.env.REACT_APP_BASE_URL_IMAGE}${team.image}`}
                    />
                  ) : (
                    <div
                      className={styles.avatarImg}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--c-card-border)',
                        fontFamily: 'Satoshi, sans-serif',
                        fontWeight: 700,
                        fontSize: 18,
                        color: 'var(--c-text-0)',
                      }}
                    >
                      {initials}
                    </div>
                  )}
                </div>
                <div className={styles.avatarBorder} />
              </div>

              {/* BUG-A11Y-015: heading h1 semantic (era <p>) */}
              <h1 className={styles.teamName}>{team.name}</h1>

              {levelLabel && (
                <div className={styles.categoryBadge}>
                  <p className={styles.categoryBadgeLabel}>{levelLabel}</p>
                </div>
              )}

              {/* Star */}
              <div style={{ marginLeft: 8, display: 'flex', alignItems: 'center' }}>
                {isStarLoading ? (
                  <Spinner size="xs" />
                ) : team.isPreferred ? (
                  <StarIcon
                    weight="fill"
                    size={24}
                    color="#FFD15A"
                    style={{ cursor: 'pointer' }}
                    onClick={onClickStar}
                  />
                ) : (
                  <StarIcon
                    size={24}
                    color="#4d4d4d"
                    style={{ cursor: 'pointer' }}
                    onClick={onClickStar}
                  />
                )}
              </div>
            </div>

            <div className={styles.titleActions}>
              <ShowByRole roles={['isAdmin', 'isDirector']}>
                <button
                  onClick={() => setIsSettingsModalOpen(true)}
                  className={styles.actionBtn}
                >
                  <div className={styles.actionBtnInner}>
                    <div className={styles.iconSm}>
                      <SlidersHorizontalIcon size={16} color="#4d4d4d" />
                    </div>
                    <p className={styles.actionBtnLabelMuted}>Impostazioni</p>
                  </div>
                </button>
              </ShowByRole>
            </div>
          </div>

          {/* Tabs and controls */}
          <div className={styles.tabsRow}>
            <div className={styles.tabsLeft}>
              <div className={styles.tabGroup}>
                {visibleTabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => handleSetActiveTab(tab)}
                    className={[styles.tabBtn, activeTab === tab ? styles.tabBtnActive : ''].filter(Boolean).join(' ')}
                  >
                    <div className={styles.tabBtnInner}>
                      <p className={[styles.tabLabel, activeTab === tab ? styles.tabLabelActive : ''].filter(Boolean).join(' ')}>
                        {tab === 'storico-clinico' ? 'Storico clinico'
                      : tab === 'fastidi' ? 'Fastidi'
                      : tab === 'afi' ? 'AFI'
                      : tab === 'carichi' ? 'Carichi'
                      : tab === 'sforzo' ? 'Sforzo'
                      : tab === 'metriche-custom' ? 'Metriche custom'
                      : tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {activeTab === 'readiness' ? (
              <div className={styles.viewToggle}>
                <button
                  onClick={() => setReadinessViewMode('matrix')}
                  className={[styles.viewToggleBtn, readinessViewMode === 'matrix' ? styles.viewToggleBtnActive : ''].filter(Boolean).join(' ')}
                >
                  <TableIcon className={styles.viewToggleIcon} />
                  <span>Matrice</span>
                </button>
                <button
                  onClick={() => setReadinessViewMode('card')}
                  className={[styles.viewToggleBtn, readinessViewMode === 'card' ? styles.viewToggleBtnActive : ''].filter(Boolean).join(' ')}
                >
                  <LayoutGrid className={styles.viewToggleIcon} />
                  <span>Card</span>
                </button>
              </div>
            ) : activeTab === 'testing' ? (
              <div className={styles.viewToggle}>
                <button
                  onClick={() => setIsTestConfigOpen(true)}
                  className={styles.viewToggleBtn}
                  title="Configura test"
                >
                  <Settings2 className={styles.viewToggleIcon} />
                  <span>Modifica test</span>
                </button>
                <button
                  onClick={() => setTestingViewMode('entry')}
                  className={[styles.viewToggleBtn, testingViewMode === 'entry' ? styles.viewToggleBtnActive : ''].filter(Boolean).join(' ')}
                >
                  <TableIcon className={styles.viewToggleIcon} />
                  <span>Dati</span>
                </button>
                <button
                  onClick={() => setTestingViewMode('analytics')}
                  className={[styles.viewToggleBtn, testingViewMode === 'analytics' ? styles.viewToggleBtnActive : ''].filter(Boolean).join(' ')}
                >
                  <BarChart3 className={styles.viewToggleIcon} />
                  <span>Analitiche</span>
                </button>
                <button
                  onClick={() => setTestingViewMode('profiling')}
                  className={[styles.viewToggleBtn, testingViewMode === 'profiling' ? styles.viewToggleBtnActive : ''].filter(Boolean).join(' ')}
                >
                  <TargetIcon size={16} className={styles.viewToggleIcon} />
                  <span>Profiling</span>
                </button>
              </div>
            ) : null}
          </div>

          {/* Tab content */}
          {activeTab === 'panoramica' ? (
            <InformationsTab teamQuery={teamQuery} creatingPdf={false} onGoTo={handleSetActiveTab} />
          ) : activeTab === 'readiness' ? (
            <TeamReadinessTab
              team={team}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              viewMode={readinessViewMode}
              onGoToEffort={() => handleSetActiveTab('sforzo')}
            />
          ) : activeTab === 'testing' ? (
            testingViewMode === 'profiling' ? (
              <TeamProfilingTab
                teamId={team._id}
                teamUsers={(team.users ?? []) as any[]}
              />
            ) : (
              <TestingView
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                mode={testingViewMode}
                setMode={(m) => setTestingViewMode(m as 'entry' | 'analytics')}
                teamUsers={(team.users ?? []) as any[]}
                teamId={team._id}
                onOpenTestConfig={() => setIsTestConfigOpen(true)}
              />
            )
          ) : activeTab === 'atleti' ? (
            <AthletesTab teamQuery={teamQuery} />
          ) : activeTab === 'attivita' ? (
            <TeamActivitiesTab teamId={team._id} />
          ) : activeTab === 'programmazione' ? (
            <ProgrammingTabs teamId={team._id} />
          ) : activeTab === 'allenamenti' ? (
            <AllenamentiPage teamId={team._id} />
          ) : activeTab === 'calendario' ? (
            <TeamCalendarTab teamId={team._id} team={team} />
          ) : activeTab === 'carichi' ? (
            <CarichiTeamTab
              teamId={team._id}
              teamName={team.name}
              teamUsers={(team.users ?? []).map((u: any) => (typeof u === 'string' ? u : u._id))}
            />
          ) : activeTab === 'sforzo' ? (
            // Click atleta → drawer col drill-down Sforzo (in-place, gestito
            // internamente da TeamEffortTab; niente più navigazione a pagina)
            <TeamEffortTab teamId={team._id} />
          ) : activeTab === 'fastidi' ? (
            <div>
              {/* Sub-tab Fastidi — riusa le classi esistenti del view-toggle (niente più stile inline) */}
              <div className={styles.fastidiSubTabRow}>
                <div className={styles.viewToggle}>
                  {(['heatmap', 'analytics'] as const).map((sub) => (
                    <button
                      key={sub}
                      onClick={() => setFastidiSubTab(sub)}
                      className={[styles.viewToggleBtn, fastidiSubTab === sub ? styles.viewToggleBtnActive : ''].filter(Boolean).join(' ')}
                    >
                      <span>{sub === 'heatmap' ? 'Heatmap team' : 'Analytics'}</span>
                    </button>
                  ))}
                </div>
              </div>
              {fastidiSubTab === 'heatmap' ? (
                <TeamBodyMapHeatmap teamId={team._id} />
              ) : (
                <TeamBodyMapAnalytics teamId={team._id} />
              )}
            </div>
          ) : activeTab === 'storico-clinico' ? (
            <div>
              {/* Aggiungi infortunio — select atleta (Chakra NativeSelect) + CustomButton, colori da token */}
              <div className={styles.injuryToolbarRow}>
                <div className={styles.injurySelectGroup}>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field
                      className={[styles.athleteSelectField, addInjuryAthleteId ? styles.athleteSelectFieldActive : ''].filter(Boolean).join(' ')}
                      value={addInjuryAthleteId}
                      onChange={(e) => setAddInjuryAthleteId(e.target.value)}
                    >
                      <option value="">Tutto il team</option>
                      {teamAthletes.map(a => (
                        <option key={a._id} value={a._id}>{a.firstName} {a.lastName}</option>
                      ))}
                    </NativeSelect.Field>
                  </NativeSelect.Root>
                  {addInjuryAthleteId && (
                    <CustomButton size="sm" color="outline" onClick={() => setAddInjuryAthleteId('')}>
                      ✕ Deseleziona
                    </CustomButton>
                  )}
                  <CustomButton
                    size="sm"
                    color="secondary"
                    icon={<PlusIcon size={14} weight="bold" />}
                    iconPosition="left"
                    disabled={!addInjuryAthleteId}
                    onClick={() => { if (addInjuryAthleteId) setAddInjuryOpen(true); }}
                  >
                    Aggiungi infortunio
                  </CustomButton>
                </div>
              </div>

              {/* Period filter — componente condiviso InjuryPeriodFilter */}
              <div className={styles.periodFilterRow}>
                <InjuryPeriodFilter value={injuryPeriod} onChange={setInjuryPeriod} />
              </div>

              {addInjuryAthleteId ? (
                /* Vista per-atleta: quando è selezionato un atleta specifico */
                <div>
                  {/* Anamnesi / storico clinico */}
                  <AnamnesisCard athleteId={addInjuryAthleteId} />

                  {/* Timeline infortuni individuale */}
                  <InjuryTimeline
                    athleteId={addInjuryAthleteId}
                    teamId={team._id}
                    period={injuryPeriod !== 'all' ? injuryPeriod : undefined}
                  />

                  {/* Grafici analitici individuali */}
                  <AthleteInjuryCharts
                    athleteId={addInjuryAthleteId}
                    period={injuryPeriod !== 'all' ? injuryPeriod : undefined}
                  />
                </div>
              ) : (
                /* Vista team: nessun atleta selezionato */
                <div>
                  {/* Insights — loading, error e empty espliciti (mai "Nessun dato" mentre è ancora in fetch) */}
                  {insightsLoading ? (
                    <div className={styles.insightsPanel}>
                      <LeopartLoader size="sm" />
                    </div>
                  ) : insightsError ? (
                    <ChartErrorState onRetry={insightsRefetch} message="Impossibile caricare gli insights" />
                  ) : insightsData?.insights?.length ? (
                    <TeamInsightsCard insights={insightsData.insights} />
                  ) : (
                    <ChartEmptyState />
                  )}

                  {/* Timeline lista infortuni */}
                  <TeamInjuryTimeline teamId={team._id} period={injuryPeriod !== 'all' ? injuryPeriod : undefined} />

                  {/* 12 chart aggregati */}
                  <TeamInjuryCharts teamId={team._id} period={injuryPeriod !== 'all' ? injuryPeriod : undefined} />
                </div>
              )}
            </div>
          ) : activeTab === 'afi' ? (
            <AFITab team={team} />
          ) : activeTab === 'metriche-custom' ? (
            <TeamCustomMetricsSection teamId={team._id} />
          ) : null}
        </div>
      </div>

      {/* Team Settings Modal */}
      <TeamSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        team={team}
        onDeleteTeam={handleDeleteTeam}
      />

      {/* Test Config Modal */}
      <TestConfigModal
        isOpen={isTestConfigOpen}
        onClose={() => setIsTestConfigOpen(false)}
        team={team}
      />

      {/* Injury Registration Modal */}
      {addInjuryOpen && addInjuryAthleteId && (
        <InjuryRegistrationModal
          isOpen={addInjuryOpen}
          onClose={() => { setAddInjuryOpen(false); setAddInjuryAthleteId(''); }}
          athleteId={addInjuryAthleteId}
          teamId={team._id}
          onSuccess={() => { setAddInjuryOpen(false); setAddInjuryAthleteId(''); }}
        />
      )}
    </div>
    </DataProvider>
  );
}
